import http from './http';

export async function listDocuments({ includeInactive = false } = {}) {
  if (!includeInactive) {
    const { data } = await http.get('/ai-documents');
    return data;
  }
  const [activeRes, inactiveRes] = await Promise.all([
    http.get('/ai-documents', { params: { active: true } }),
    http.get('/ai-documents', { params: { active: false } }),
  ]);
  const active = activeRes.data?.documents || [];
  const inactive = inactiveRes.data?.documents || [];
  const byId = new Map();
  [...active, ...inactive].forEach((d) => {
    if (d?.id != null) byId.set(d.id, d);
  });
  const documents = Array.from(byId.values()).sort((a, b) => (a.id || 0) - (b.id || 0));
  return { success: true, documents };
}

export async function createDocument(payload) {
  const { data } = await http.post('/ai-documents', payload);
  return data;
}

export async function checkUploadDocument(formData) {
  const { data } = await http.post('/ai-documents/check-upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
}

export async function uploadDocument(formData) {
  const { data } = await http.post('/ai-documents/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
}

/**
 * O FAVORITO DO OPERADOR (Gisele, 23/10/2026).
 *
 * Não confundir com fixar: fixar é o destaque INSTITUCIONAL, tem permissão
 * (`#fixar-material-da-base`) e muda o que a equipe inteira vê primeiro.
 * Favoritar muda a ordem na tela de quem clicou, e só dela -- por isso não
 * tem permissão e por isso é outro endpoint, e não um campo do `update`.
 *
 * Dois verbos em vez de um valor: o estado é "existe o vínculo" ou "não
 * existe", e cada verbo diz um deles. Mandar `{favorito: false}` dependeria
 * de a tela saber o valor atual -- e dois cliques rápidos mandariam o mesmo
 * duas vezes, deixando a estrela discordando do banco.
 */
export async function favoritarMaterial(id) {
  const { data } = await http.post(`/ai-documents/${id}/favorito`);
  return data;
}

export async function desfavoritarMaterial(id) {
  const { data } = await http.delete(`/ai-documents/${id}/favorito`);
  return data;
}

/**
 * Os limites de envio DA BASE, ditos pelo servidor.
 *
 * Documento e vídeo não têm o mesmo teto (ver `PoliticaDoArquivoDaBase`), e o
 * do vídeo é configurável por empresa. Escrever os números na tela seria
 * repetir uma decisão que já mora no servidor -- e os dois divergiriam no dia
 * em que alguém mudasse o limite nas configurações.
 *
 * Devolve `null` quando a chamada falha: quem usa trata ausência como "não
 * sei", e não como zero. Zero recusaria todo arquivo por causa de um erro de
 * rede.
 */
export async function limitesDaBase() {
  try {
    const { data } = await http.get('/politica-de-anexos');
    return data?.base_de_conhecimento || null;
  } catch (e) {
    return null;
  }
}

/**
 * O arquivo do material como endereço tocável pelo `<video>`.
 *
 * ## Por que passa por aqui, e não vai direto no `src`
 *
 * Porque a rota do arquivo é autenticada por JWT no cabeçalho, e o `<video>`
 * busca o endereço SOZINHO, sem cabeçalho nenhum -- responderia 401. Então a
 * tela baixa com o token e entrega ao player um endereço de memória.
 *
 * ## O que isto custa
 *
 * O vídeo inteiro entra na memória do navegador antes de começar a tocar, e
 * não dá para arrastar a barra para um trecho ainda não baixado. Para conferir
 * o que se acabou de subir, serve. Quem assiste de verdade é o cliente, pela
 * página pública -- e lá a rota responde a `Range`, em pedaços, sem isto.
 *
 * Quem chama PRECISA devolver o endereço com `URL.revokeObjectURL` ao fechar:
 * sem isso o vídeo fica na memória da aba até alguém recarregar a página.
 */
export async function urlDoArquivoDoMaterial(material) {
  const res = await http.get(`/ai-documents/${material.id}/download`, { responseType: 'blob' });
  const blob = res.data instanceof Blob ? res.data : new Blob([res.data]);

  return URL.createObjectURL(blob);
}

export async function updateDocument(id, payload) {
  const { data } = await http.put(`/ai-documents/${id}`, payload);
  return data;
}

export async function deleteDocument(id) {
  const { data } = await http.delete(`/ai-documents/${id}`);
  return data;
}

export async function getIndexStatus(id) {
  const { data } = await http.get(`/ai-documents/${id}/index-status`);
  return data;
}

// Status de vários docs numa requisição só (evita estourar o throttle:api).
export async function getIndexStatusBatch(ids) {
  const { data } = await http.post('/ai-documents/index-status-batch', { ids });
  return data;
}

export async function reprocessDocument(id) {
  const { data } = await http.post(`/ai-documents/${id}/reprocess`);
  return data;
}

// Reprocessa no servidor em sequência — pode demorar; timeout ampliado.
export async function reprocessAll() {
  const { data } = await http.post('/ai-documents/reprocess-all', {}, { timeout: 600000 });
  return data;
}

// Processa apenas os pendentes (não indexados), numa única requisição server-side.
export async function reprocessPending() {
  const { data } = await http.post('/ai-documents/reprocess-all', { only_pending: true }, { timeout: 600000 });
  return data;
}

// Reprocessa um LOTE específico de ids (usado no processamento em lotes com progresso).
export async function reprocessDocuments(ids) {
  const { data } = await http.post('/ai-documents/reprocess-all', { ids }, { timeout: 120000 });
  return data;
}

export async function semanticSearch(query, limit = 5) {
  const { data } = await http.post('/ai-search', { query, limit });
  return data;
}

export async function chatTest(message, extra = {}) {
  const { data } = await http.post('/ai-assistant/chat', {
    message,
    ...extra,
  });
  return data;
}

export async function listCategories() {
  const { data } = await http.get('/ai-documents/categories/list');
  return data;
}

export async function listTags() {
  const { data } = await http.get('/ai-documents/tags/list');
  return data;
}

export async function downloadDocuments(docs, { onProgress } = {}) {
  const withFile = (docs || []).filter((d) => d?.id != null && (d.file_path || d.original_filename));
  for (let i = 0; i < withFile.length; i++) {
    await downloadDocument(withFile[i]);
    onProgress?.(i + 1, withFile.length);
    if (i < withFile.length - 1) {
      await new Promise((r) => setTimeout(r, 350));
    }
  }
  return { total: withFile.length, skipped: (docs || []).length - withFile.length };
}

export async function downloadDocument(doc) {
  const res = await http.get(`/ai-documents/${doc.id}/download`, { responseType: 'blob' });
  const blob = res.data instanceof Blob ? res.data : new Blob([res.data]);
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  const name = doc.original_filename || doc.title || `documento-${doc.id}`;
  link.setAttribute('download', name);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

/**
 * Só os vídeos da base de conhecimento.
 *
 * O recorte é do servidor (`?tipo=`), e não um `.filter()` aqui, porque a
 * listagem completa carrega o conteúdo inteiro de cada documento -- trazer
 * tudo para descartar no navegador é pagar a transferência da base inteira
 * para mostrar meia dúzia de vídeos.
 */
export async function listVideos() {
  const { data } = await http.get('/ai-documents', { params: { tipo: 'video' } });
  return data;
}

/**
 * Só os documentos — a outra aba de "Vídeos e treinamentos".
 *
 * Mesmo recorte do servidor, pelo lado oposto: material SEM endereço de vídeo.
 * As duas abas particionam a base, então nenhum material fica fora das duas e
 * nenhum aparece nas duas.
 *
 * Os fixados já chegam primeiro — quem ordena é o servidor (`scopeOrdered`), e
 * não a tela: ordenar aqui deixaria o primeiro item mudando de lugar depois da
 * carga.
 */
export async function listDocumentosDaBase() {
  const { data } = await http.get('/ai-documents', { params: { tipo: 'documento' } });
  return data;
}

/*
 * A CENTRAL DE AJUDA (20/10/2026).
 *
 * Decisão do dono do produto, depois de validarmos a central que a SysFar
 * mantém no Movidesk: reproduzir aquela navegação com o nosso desenho, para ser
 * a base que a equipe alimenta e que alimenta o Orby.
 *
 * Note que ESCREVER artigo não está aqui: usa `createDocument`/`updateDocument`,
 * que falam com `/ai-documents` -- o endpoint que indexa no Qdrant. Um caminho
 * de escrita próprio criaria artigo que o Orby não acha.
 */

/** A árvore de seções, com a conta de artigos de cada uma. */
export async function listarSecoes() {
  const { data } = await http.get('/central-de-ajuda/categorias');
  return data;
}

/**
 * Os artigos de uma seção (ou da busca), paginados.
 *
 * O recorte e a paginação são do SERVIDOR: a base tem centenas de artigos, e
 * cada um pode ser a transcrição inteira de um procedimento.
 */
export async function listarArtigos(params = {}) {
  const { data } = await http.get('/central-de-ajuda/artigos', { params });
  return data;
}

/** Um artigo inteiro, com a trilha até ele. */
export async function lerArtigo(id) {
  const { data } = await http.get(`/central-de-ajuda/artigos/${id}`);
  return data;
}

export async function criarSecao(payload) {
  const { data } = await http.post('/central-de-ajuda/categorias', payload);
  return data;
}

export async function renomearSecao(id, payload) {
  const { data } = await http.put(`/central-de-ajuda/categorias/${id}`, payload);
  return data;
}

export async function apagarSecao(id) {
  const { data } = await http.delete(`/central-de-ajuda/categorias/${id}`);
  return data;
}
